import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import {
  Calendar as CalIcon, Users, Package, User, Mail, Phone, MapPin, Baby,
  AlertTriangle, ShieldCheck, Gift, DollarSign,
} from 'lucide-react';
import { TAX_RATE } from '../constants/bookingCatalog';
import { BookingDiscountsWidget } from './BookingDiscountsWidget';
import {
  AddExtrasMenu, computeAddOnsTotal, computeCustomExtrasTotal,
  buildAddOnsPayload, buildCustomChargesPayload, buildDesignsPayload, seedFromBooking,
} from './AddExtrasMenu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIME_LABELS = { '10:00': '10:00 AM', '13:00': '1:00 PM', '16:00': '4:00 PM' };

/**
 * Shared admin dialog for editing a booking in full.
 * Props:
 *  - booking: full booking document
 *  - open, onClose
 *  - onSaved(updatedBooking): callback after a successful save
 *  - packages: [{id,name,price}] from /api/packages
 *  - employees: [{user_id,name}]
 */
export const EditBookingDialog = ({ booking, open, onClose, onSaved, packages = [], employees = [] }) => {
  const [form, setForm] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const [extrasQty, setExtrasQty] = useState({});
  const [customExtras, setCustomExtras] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [reason, setReason] = useState('');

  // Load the dynamic add-ons catalog once (shared with the public booking page).
  useEffect(() => {
    if (!open || catalog) return;
    axios.get(`${API_URL}/api/addons/catalog`)
      .then((r) => setCatalog(r.data || { categories: [] }))
      .catch(() => setCatalog({ categories: [] }));
  }, [open, catalog]);

  // Seed state from booking when dialog opens
  useEffect(() => {
    if (!booking || !open) return;
    setForm({
      customer_name: booking.customer_name || '',
      customer_email: booking.customer_email || '',
      customer_phone: booking.customer_phone || '',
      customer_address: booking.customer_address || '',
      child_name: booking.child_name || '',
      child_age: booking.child_age || '',
      date: booking.date || '',
      time_slot: booking.time_slot || '10:00',
      package_id: booking.package_id || (packages[0]?.id ?? ''),
      guest_count: booking.guest_count || 1,
      edible_notes: booking.edible_notes || '',
      booked_by_employee_id: booking.booked_by_employee_id || '',
      allergy_form_completed: !!booking.allergy_form_completed,
      booking_id: booking.booking_id,
      discount: booking.discount || null,
    });
    const seeded = seedFromBooking(booking);
    setSelectedAddOns(seeded.selectedAddOns);
    setExtrasQty(seeded.extrasQty);
    setCustomExtras(seeded.customExtras);
    setDesigns(seeded.designs);
    setSendEmail(false);
    setReason('');
  }, [booking, open, packages]);

  // Live totals preview
  const totals = useMemo(() => {
    if (!form) return null;
    const pkg = packages.find((p) => p.id === form.package_id) || { price: 0 };
    const subtotal = Number(pkg.price || 0);
    const addOnsTotal = computeAddOnsTotal(catalog, selectedAddOns, extrasQty);
    const customTotal = computeCustomExtrasTotal(customExtras);
    const preDiscount = subtotal + addOnsTotal + customTotal;
    let discountAmount = 0;
    if (form.discount && form.discount.amount) discountAmount = Number(form.discount.amount) || 0;
    const afterDiscount = Math.max(0, preDiscount - discountAmount);
    const tax = Math.round(afterDiscount * TAX_RATE * 100) / 100;
    const total = Math.round((afterDiscount + tax) * 100) / 100;
    return {
      packagePrice: Number(pkg.price || 0),
      addOnsTotal: Math.round(addOnsTotal * 100) / 100,
      customTotal: Math.round(customTotal * 100) / 100,
      preDiscount: Math.round(preDiscount * 100) / 100,
      discountAmount: Math.round(discountAmount * 100) / 100,
      subtotal: Math.round(afterDiscount * 100) / 100,
      tax,
      total,
      deposit: Math.round(total * 0.5 * 100) / 100,
    };
  }, [form, catalog, selectedAddOns, extrasQty, customExtras, packages]);

  if (!booking) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form) return;
    const payload = {
      customer_name: form.customer_name.trim(),
      customer_email: form.customer_email.trim(),
      customer_phone: form.customer_phone.trim(),
      customer_address: (form.customer_address || '').trim() || null,
      child_name: form.child_name.trim(),
      child_age: Number(form.child_age) || 1,
      date: form.date,
      time_slot: form.time_slot,
      package_id: form.package_id,
      guest_count: Number(form.guest_count) || 1,
      edible_notes: form.edible_notes || null,
      booked_by_employee_id: form.booked_by_employee_id || '',
      add_ons: buildAddOnsPayload(catalog, selectedAddOns, extrasQty),
      custom_charges: buildCustomChargesPayload(customExtras),
      designs: buildDesignsPayload(designs),
      admin_reason: reason || 'Admin booking edit',
      send_updated_invoice: sendEmail,
    };

    setSaving(true);
    try {
      const res = await axios.patch(`${API_URL}/api/admin/bookings/${form.booking_id}`, payload, { withCredentials: true });
      toast.success('Booking updated' + (sendEmail ? ' · customer notified' : ''));
      onSaved?.(res.data?.booking || null);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving && !o) onClose?.(); }}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-booking-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Fredoka, sans-serif' }} className="flex items-center gap-2 text-2xl">
            <Gift className="w-6 h-6 text-[#FF3B6B]" /> Edit Booking
          </DialogTitle>
          <DialogDescription>
            Update any field below — totals recalculate instantly and a new invoice is generated on save.
          </DialogDescription>
        </DialogHeader>

        {form && (
          <div className="space-y-5">
            {/* ─── Customer Info ─── */}
            <section className="rounded-2xl border border-[#E8E5DF] p-4 space-y-3">
              <h3 className="flex items-center gap-2 font-semibold text-[#1E1E24]"><User className="w-4 h-4" /> Customer Info</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Name" icon={<User className="w-3.5 h-3.5" />}>
                  <Input value={form.customer_name} onChange={(e) => set('customer_name', e.target.value)} className="mt-1" data-testid="eb-customer-name" />
                </Field>
                <Field label="Email" icon={<Mail className="w-3.5 h-3.5" />}>
                  <Input type="email" value={form.customer_email} onChange={(e) => set('customer_email', e.target.value)} className="mt-1" data-testid="eb-customer-email" />
                </Field>
                <Field label="Phone" icon={<Phone className="w-3.5 h-3.5" />}>
                  <Input value={form.customer_phone} onChange={(e) => set('customer_phone', e.target.value)} className="mt-1" data-testid="eb-customer-phone" />
                </Field>
                <Field label="Address (optional)" icon={<MapPin className="w-3.5 h-3.5" />}>
                  <Input value={form.customer_address} onChange={(e) => set('customer_address', e.target.value)} className="mt-1" placeholder="Street, City, Province" data-testid="eb-customer-address" />
                </Field>
              </div>
            </section>

            {/* ─── Party Details ─── */}
            <section className="rounded-2xl border border-[#E8E5DF] p-4 space-y-3">
              <h3 className="flex items-center gap-2 font-semibold text-[#1E1E24]"><CalIcon className="w-4 h-4" /> Party Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Birthday child's name" icon={<Baby className="w-3.5 h-3.5" />}>
                  <Input value={form.child_name} onChange={(e) => set('child_name', e.target.value)} className="mt-1" data-testid="eb-child-name" />
                </Field>
                <Field label="Child's age">
                  <Input type="number" min="1" max="25" value={form.child_age} onChange={(e) => set('child_age', e.target.value)} className="mt-1" data-testid="eb-child-age" />
                </Field>
                <Field label="Date">
                  <Input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} className="mt-1" data-testid="eb-date" />
                </Field>
                <Field label="Time slot">
                  <Select value={form.time_slot} onValueChange={(v) => set('time_slot', v)}>
                    <SelectTrigger className="mt-1 bg-white" data-testid="eb-time"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {Object.entries(TIME_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Package" icon={<Package className="w-3.5 h-3.5" />}>
                  <Select value={form.package_id} onValueChange={(v) => set('package_id', v)}>
                    <SelectTrigger className="mt-1 bg-white" data-testid="eb-package"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {packages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} (${p.price})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Guest count" icon={<Users className="w-3.5 h-3.5" />}>
                  <Input type="number" min="1" max="50" value={form.guest_count} onChange={(e) => set('guest_count', e.target.value)} className="mt-1" data-testid="eb-guests" />
                </Field>
                <Field label="Assigned employee" className="md:col-span-2">
                  <Select value={form.booked_by_employee_id || 'self'} onValueChange={(v) => set('booked_by_employee_id', v === 'self' ? '' : v)}>
                    <SelectTrigger className="mt-1 bg-white" data-testid="eb-employee"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="self">Self-service (customer)</SelectItem>
                      {employees.map((e) => <SelectItem key={e.user_id} value={e.user_id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Notes / allergy-safe prep">
                <Textarea value={form.edible_notes} onChange={(e) => set('edible_notes', e.target.value)} rows={2} className="mt-1" data-testid="eb-notes" />
              </Field>
            </section>

            {/* ─── Allergy form status ─── */}
            {form.allergy_form_completed ? (
              <div className="rounded-2xl border border-green-200 bg-green-50 p-3 flex items-center gap-2 text-sm text-green-700" data-testid="eb-allergy-done">
                <ShieldCheck className="w-4 h-4" /> Allergy &amp; consent form completed.
              </div>
            ) : (
              <div className="rounded-2xl border border-yellow-300 bg-yellow-50 p-3 flex items-center justify-between gap-2 text-sm text-yellow-800" data-testid="eb-allergy-missing">
                <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Allergy form not completed yet.</span>
                <a
                  className="underline font-medium"
                  href={`/booking/${form.booking_id}/allergy-form`}
                  target="_blank" rel="noreferrer"
                  data-testid="eb-allergy-link"
                >
                  Complete now →
                </a>
              </div>
            )}

            {/* ─── Add Extras (optional) — unified menu ─── */}
            <section className="rounded-2xl border border-[#E8E5DF] p-4 space-y-3">
              <h3 className="flex items-center gap-2 font-semibold text-[#1E1E24]"><Gift className="w-4 h-4" /> Add Extras <span className="text-sm font-normal text-[#6B6B7A]">(optional)</span></h3>
              <AddExtrasMenu
                catalog={catalog}
                selectedAddOns={selectedAddOns} setSelectedAddOns={setSelectedAddOns}
                extrasQty={extrasQty} setExtrasQty={setExtrasQty}
                customExtras={customExtras} setCustomExtras={setCustomExtras}
                designs={designs} setDesigns={setDesigns}
                childName={form.child_name}
              />
            </section>

            {/* ─── Totals preview ─── */}
            {totals && (
              <section className="rounded-2xl border border-[#E8E5DF] bg-[#FFF9F5] p-4 space-y-1 text-sm" data-testid="eb-totals">
                <Row label="Package" value={`$${totals.packagePrice.toFixed(2)}`} />
                <Row label="Add-ons" value={`$${totals.addOnsTotal.toFixed(2)}`} />
                <Row label="Custom extras" value={`$${totals.customTotal.toFixed(2)}`} />
                {totals.discountAmount > 0 && (
                  <Row label={`Discount (${form.discount?.code || ''})`} value={`-$${totals.discountAmount.toFixed(2)}`} muted color="#06D6A0" />
                )}
                <Row label="Tax (HST 13%)" value={`$${totals.tax.toFixed(2)}`} muted />
                <div className="border-t border-[#E8E5DF] my-1" />
                <Row label={<span className="font-semibold">Total</span>} value={<span className="font-bold text-[#1E1E24]">${totals.total.toFixed(2)}</span>} />
                <Row label="50% deposit" value={`$${totals.deposit.toFixed(2)}`} muted />
              </section>
            )}

            {/* ─── Adjustments: discount + gift card (PIN-gated) ─── */}
            <section className="rounded-2xl border border-[#E8E5DF] p-4" data-testid="eb-adjustments">
              <p className="text-[10px] uppercase tracking-widest text-[#6B6B7A] font-semibold mb-3">Adjustments</p>
              <BookingDiscountsWidget
                booking={booking}
                mode="admin"
                onUpdated={(b) => { if (b) { onSaved?.(b); } }}
              />
              <p className="text-[11px] text-[#6B6B7A] mt-3 italic">PIN required. Changes recompute totals + balance due automatically.</p>
            </section>

            {/* ─── Save options ─── */}
            <section className="rounded-2xl border border-[#E8E5DF] p-4 space-y-3">
              <Field label="Reason (audit log)">
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g., Customer added 2 extra pizzas" className="mt-1" data-testid="eb-reason" />
              </Field>
              <label className="flex items-center gap-2 text-sm text-[#1E1E24]">
                <Checkbox checked={sendEmail} onCheckedChange={(v) => setSendEmail(!!v)} data-testid="eb-send-email" />
                <Mail className="w-4 h-4 text-[#6366F1]" />
                Email updated invoice to the customer after saving
              </label>
            </section>

            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="flex-1 rounded-full" disabled={saving} data-testid="eb-cancel">Cancel</Button>
              <Button onClick={save} disabled={saving} className="flex-1 rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="eb-save">
                <DollarSign className="w-4 h-4 mr-1" />
                {saving ? 'Saving…' : 'Save Booking'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, icon, children, className = '' }) => (
  <div className={className}>
    <Label className="text-xs text-[#6B6B7A] flex items-center gap-1">{icon}{label}</Label>
    {children}
  </div>
);

const Row = ({ label, value, muted = false, color }) => (
  <div className="flex items-center justify-between">
    <span className={muted ? 'text-[#6B6B7A]' : 'text-[#1E1E24]'} style={color ? { color } : undefined}>{label}</span>
    <span className={muted ? 'text-[#6B6B7A]' : 'text-[#1E1E24]'} style={color ? { color } : undefined}>{value}</span>
  </div>
);

export default EditBookingDialog;
